<template>
  <v-dialog
      v-model="dialog"
      hide-overlay
      fullscreen
  >
    <v-card elevation="0">
      <v-col class="text-right px-4">
        <v-btn icon @click="closeDialog">
          <v-icon large color="primary">close</v-icon>
        </v-btn>
      </v-col>

      <v-card-text>
        <v-form ref="form">
          <div class="pa-5">
            <h3>{{ $t('components:minimized-customer-form.title') }}</h3>

            <v-row justify="space-around">
              <v-col cols="12" md="5">
                <v-radio-group row v-model="customer.type" :rules="[ruleRequired($t('utils:rules.required'))]">
                  <v-radio :label="$t('common:customer')" value="CUSTOMER"/>
                  <v-radio :label="$t('components:minimized-customer-form.labels.supplier')" value="SUPPLIER"/>
                </v-radio-group>

                <!-- Name -->
                <DuplicateCheckField
                    ref="duplName"
                    :value="customer.name"
                    :label="$t('common:name')"
                    item-additional-info-field="street"
                    required
                    tab-index="1"
                    item-text="name"
                    @input="customer.name = $event"
                    @checkForDuplicates="checkByName"
                    trigger-focus
                    hide-show-btn
                />

                <v-text-field
                    outlined
                    v-model="customer.regNumber"
                    :label="$t('components:minimized-customer-form.labels.reg-number')"
                    clearable
                    tabindex="2"
                />

                <v-text-field
                    outlined
                    v-model="customer.vatId"
                    :label="$t('components:minimized-customer-form.labels.vatin')"
                    clearable
                    tabindex="10"
                />

                <h4 class="primary--text font-weight-bold">
                  {{ $t('common:address') }}
                </h4>
                <DuplicateCheckField
                    ref="duplAddress"
                    :value="customer.street"
                    :label="$t('common:street')"
                    item-additional-info-field="street"
                    required
                    tab-index="3"
                    item-text="name"
                    @input="customer.street = $event"
                    @checkForDuplicates="checkByAddress"
                    hide-show-btn
                />

                <v-row justify="space-around">
                  <v-col cols="6" md="4">
                    <v-text-field
                        outlined
                        v-model="customer.zip"
                        :label="$t('common:zip')"
                        :rules="[ruleRequired($t('utils:rules.required'))]"
                        clearable
                        tabindex="4"
                        @input="getCityProvinceByZIP"
                    />
                  </v-col>
                  <v-col cols="6" md="8">
                    <v-text-field
                        outlined
                        v-model="customer.city"
                        :label="$t('common:city')"
                        :rules="[ruleRequired($t('utils:rules.required'))]"
                        clearable
                        tabindex="5"
                    />
                  </v-col>
                </v-row>
                <!-- Input Select Country -->
                <v-select
                    :items="restCountry"
                    item-text="translatedName"
                    item-value="id"
                    :menu-props="{ bottom: true, offsetY: true }"
                    v-model="customer.country"
                    outlined
                    :label="$t('common:country')"
                    clearable
                    :rules="[ruleRequired($t('utils:rules.required'))]"
                    @change="updateProvince"
                    tabindex="6"
                />
                <v-select
                    :items="restProvince"
                    item-text="translatedName"
                    item-value="id"
                    :menu-props="{ bottom: true, offsetY: true }"
                    v-model="customer.province"
                    outlined
                    :label="$t('common:province')"
                    clearable
                    :rules="[ruleRequired($t('utils:rules.required'))]"
                    tabindex="7"
                />
              </v-col>
              <v-col cols="12" md="5">
                <h4 class="primary--text font-weight-bold">
                  {{ $t('components:minimized-customer-form.contact') }}
                </h4>
                <v-text-field
                    outlined
                    v-model="customer.contactName"
                    :label="$t('components:minimized-customer-form.labels.contact-name')"
                    clearable
                    tabindex="11"
                />
                <v-text-field
                    outlined
                    v-model="customer.contactMail"
                    :label="$t('components:minimized-customer-form.labels.contact-mail')"
                    clearable
                    tabindex="12"
                />
                <v-text-field
                    outlined
                    v-model="customer.contactPhone"
                    :label="$t('components:minimized-customer-form.labels.contact-phone')"
                    clearable
                    tabindex="13"
                />

                <BaseButton
                    color="primary"
                    :btn-text="$t('buttons:save')"
                    @clickedButton="saveCustomer()"
                />

                <BaseButton
                    color="grey"
                    :btn-text="$t('buttons:back')"
                    @clickedButton="closeDialog(true)"
                />
              </v-col>
            </v-row>
          </div>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import Customer from "@/modules/core/data/customer";
import DuplicateCheckField from "@/modules/core/components/DuplicateCheckField";
import {ruleRequired} from "@/modules/core/services/rules";
import DuplicateCheckService from "@/modules/core/services/duplicate-check-service";
import Utils from "@/modules/core/services/utils";
import CountryService from "@/modules/core/services/country-service";
import GPSUtils from "@/modules/core/services/gps-utils";
import BaseButton from "@/modules/core/components/Base/BaseButton";
import CustomerService from "@/modules/core/services/customer-service";

export default {
  name: "MinimizedCustomerDialogForm.vue",
  components: {BaseButton, DuplicateCheckField},
  data() {
    return {
      dialog: false,
      customer: new Customer(),
      restCountry: [],
      restProvince: [],
    }
  },
  computed: {
    currentLang() {
      return this.$store.state.translation.language;
    }
  },
  methods: {
    closeDialog(clearTypeahead = false) {
      this.dialog = false;
      this.$refs.form.reset();

      if (clearTypeahead) {
        this.$emit('close')
      }
    },

    openDialogWithName(name) {
      this.dialog = true;
      this.customer.name = name;
    },

    saveCustomer() {
      CustomerService.registerCustomer(this.customer,
          (res) => {
            this.$emit('onSave', res.data);
            this.closeDialog();
          },
          (error) => {
            this.$logger.error("Failed to register customer, because: " + error);
            Utils.showFailureSnackbar(this.$t('snackbar:add-failure', {name: this.$t('common:customer')}));
          }
      )
    },

    checkByName(term) {
      DuplicateCheckService.checkCustomers('NAME', term,
          (res) => {
            this.$refs.duplName.fillData(res.data);
          }
      )
    },

    checkByAddress(term) {
      DuplicateCheckService.checkCustomers('ADDRESS', term,
          (res) => {
            this.$refs.duplAddress.fillData(res.data);
          }
      )
    },

    translateCountry() {
      this.restCountry = Utils.translateAllCountries(this.restCountry);
    },

    translateProvinces() {
      this.restProvince = Utils.translateAllProvinces(this.restCountry, this.restProvince, this.customer.country);
    },

    getAllCountries() {
      CountryService.readAllFromAPI(
          (res) => {
            this.restCountry = res.data;
            this.translateCountry();

            if (this.customer.country == null) {
              this.customer.country = res.data[0].id;
              this.updateProvince();
            }
          },
          (error) => {
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:country')}))
            this.$logger.error(error);
          }
      )
    },

    updateProvince() {
      if (this.customer.country) {

        CountryService.readProvincesOfCountry(this.customer.country,
            (res) => {
              this.restProvince = res.data;
              this.translateProvinces();
            },
            (error) => {
              Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:province_plural')}))
              this.$logger.error(error);
            }
        )
      }
    },

    /**
     * Reads and sets the building city and province
     * by the entered ZIP
     */
    getCityProvinceByZIP() {
      // check if there is a zip and country set for the building
      if (this.customer.zip && this.customer.country) {

        const countryName = GPSUtils.getCountryNameById(this.customer.country, this.restCountry);

        if (GPSUtils.isValidZIP(countryName.toLowerCase(), this.customer.zip)) {
          GPSUtils.readCityProvinceByZIP(
              this.customer.zip,
              countryName,
              this.$store.state.translation.language,
              this.restProvince,
              (res) => {
                this.customer.city = res.city;
                this.customer.province = res.province;
              },
              () => {
                Utils.showFailureSnackbar(this.$t('settings/customer:edit.zip-to-city-fail'))
              }
          )
        }
      }
    },

    ruleRequired
  },
  watch: {
    currentLang() {
      Utils.sleep(500).then(() => {
        this.getAllCountries();
        this.updateProvince();
        this.$refs.form.resetValidation()
      });
    }
  },
  created() {
    this.getAllCountries();
  }
}
</script>

<style scoped lang="scss">
.v-dialog__content.v-dialog__content--active {
  z-index: 500 !important;
}
</style>